import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/firestore';
import { Account, Tenant, Settings } from 'src/models/account.model';
import { SettingsService } from './settings.service';

@Injectable({
  providedIn: 'root'
})
export class TenantService {

  constructor(
    private afs: AngularFirestore,
    private settingsService: SettingsService
  ) { }

  // Generate unique tenant ID
  generateTenantId(): string {
    return this.afs.createId();
  }

  // Create username from store name and tenant name
  createUsername(storeName: string, tenantName: string): string {
    const storeClean = storeName.toLowerCase().replace(/\W/g, '');
    const nameClean = tenantName.toLowerCase().replace(/\W/g, '');
    return `${storeClean.substring(0, 8)}${nameClean.substring(0, 4)}`;
  }

  // Generate 6-digit PIN
  generatePin(): string {
    const min = 100000;
    const max = 999999;
    return Math.floor(Math.random() * (max - min + 1) + min).toString();
  }

  // Add tenant to account settings
  async addTenant(account: Account, tenant: Tenant): Promise<void> {
    if (!account.settings) {
      account.settings = {};
    }
    if (!account.settings.tenants) {
      account.settings.tenants = [];
    }
    account.settings.tenants.push(tenant);

    // Get account_id if not available
    let accountId = account.account_id;
    if (!accountId) {
      accountId = await this.getAccountIdByKey(account.account_key);
    }

    // Update both accounts and settings collections
    await Promise.all([
      // Update settings collection (using account_key as document ID)
      this.settingsService.updateSettings(account.settings, account.account_key),
      // Update accounts collection
      accountId ? this.settingsService.addSettingsToAccount(accountId, account.settings) : Promise.resolve()
    ]);
  }

  // Helper to get account_id from account_key
  private async getAccountIdByKey(accountKey: string): Promise<string | null> {
    const snapshot = await this.afs.collection('accounts', ref =>
      ref.where('account_key', '==', accountKey).limit(1)
    ).get().toPromise();

    if (snapshot && !snapshot.empty) {
      return snapshot.docs[0].id;
    }
    return null;
  }

  // Update tenant
  async updateTenant(account: Account, tenant: Tenant): Promise<void> {
    if (!account.settings?.tenants) return;

    const index = account.settings.tenants.findIndex(t => t.tenant_id === tenant.tenant_id);
    if (index !== -1) {
      account.settings.tenants[index] = tenant;

      // Get account_id if not available
      let accountId = account.account_id;
      if (!accountId) {
        accountId = await this.getAccountIdByKey(account.account_key);
      }

      // Update both accounts and settings collections
      await Promise.all([
        // Update settings collection (using account_key as document ID)
        this.settingsService.updateSettings(account.settings, account.account_key),
        // Update accounts collection
        accountId ? this.settingsService.addSettingsToAccount(accountId, account.settings) : Promise.resolve()
      ]);
    }
  }

  // Delete tenant
  async deleteTenant(account: Account, tenantId: string): Promise<void> {
    if (!account.settings?.tenants) return;

    const index = account.settings.tenants.findIndex(t => t.tenant_id === tenantId);
    if (index !== -1) {
      account.settings.tenants.splice(index, 1);

      // Get account_id if not available
      let accountId = account.account_id;
      if (!accountId) {
        accountId = await this.getAccountIdByKey(account.account_key);
      }

      // Update both accounts and settings collections
      await Promise.all([
        // Update settings collection (using account_key as document ID)
        this.settingsService.updateSettings(account.settings, account.account_key),
        // Update accounts collection
        accountId ? this.settingsService.addSettingsToAccount(accountId, account.settings) : Promise.resolve()
      ]);
    }
  }

  // Validate tenant login
  validateTenantLogin(account: Account, username: string, pin: string): Tenant | null {
    if (!account.settings?.tenants) return null;
    return account.settings.tenants.find(
      t => t.username === username && t.password_pin === pin && t.is_active
    ) || null;
  }

  // Get tenant by ID
  getTenantById(account: Account, tenantId: string): Tenant | null {
    if (!account.settings?.tenants) return null;
    return account.settings.tenants.find(t => t.tenant_id === tenantId) || null;
  }

  // Check if username exists
  isUsernameExists(account: Account, username: string, excludeTenantId?: string): boolean {
    if (!account.settings?.tenants) return false;
    return account.settings.tenants.some(t =>
      t.username === username && t.tenant_id !== excludeTenantId
    );
  }

  // Get all tenants
  getTenants(account: Account): Tenant[] {
    return account.settings?.tenants || [];
  }

  // Increment voucher counter for tenant
  async incrementVoucherCount(account: Account, tenantId: string, count: number = 1): Promise<void> {
    const tenant = this.getTenantById(account, tenantId);
    if (tenant) {
      // Initialize voucher_settings if it doesn't exist
      if (!tenant.voucher_settings) {
        tenant.voucher_settings = {
          free_parking_hours: 1,
          generation_limit: null,
          vouchers_generated: 0,
          expiration_hours: 24
        };
      }
      tenant.voucher_settings.vouchers_generated =
        (tenant.voucher_settings.vouchers_generated || 0) + count;
      await this.updateTenant(account, tenant);

      // Update localStorage to keep data in sync
      localStorage.setItem('account', JSON.stringify(account));
    }
  }

  // Check if tenant can generate vouchers
  canGenerateVouchers(tenant: Tenant, quantity: number = 1): { allowed: boolean; reason?: string } {
    const settings = tenant.voucher_settings;
    if (!settings) return { allowed: true };

    if (settings.generation_limit !== null && settings.generation_limit !== undefined) {
      const remaining = settings.generation_limit - (settings.vouchers_generated || 0);
      if (remaining < quantity) {
        return {
          allowed: false,
          reason: remaining <= 0
            ? 'Voucher limit reached. You cannot generate more vouchers.'
            : `You can only generate ${remaining} more voucher(s).`
        };
      }
    }
    return { allowed: true };
  }

  // Update last login time - only updates if settings.tenants already exists in Firebase
  async updateLastLogin(account: Account, tenantId: string): Promise<void> {
    if (!account.settings?.tenants) return;

    const tenantIndex = account.settings.tenants.findIndex(t => t.tenant_id === tenantId);
    if (tenantIndex === -1) return;

    // Update local copy
    account.settings.tenants[tenantIndex].last_login = new Date();

    try {
      // Get account document ID
      const accountId = await this.getAccountIdByKey(account.account_key);
      if (!accountId) return;

      // Check if settings.tenants already exists in the account document before updating
      const accountDoc = await this.afs.collection<Account>('accounts').doc(accountId).get().toPromise();
      const accountData = accountDoc.data() as Account;
      if (accountDoc.exists && accountData?.settings?.tenants) {
        // Only update if settings.tenants already exists - use update() not set()
        await this.afs.collection('accounts').doc(accountId).update({
          'settings.tenants': account.settings.tenants
        });
      }
      // If settings.tenants doesn't exist, don't create it - skip the update silently
    } catch (error) {
      console.error('Error updating last login:', error);
      // Silently fail - last login update is not critical for tenant functionality
    }
  }

  // Reset voucher counter
  async resetVoucherCount(account: Account, tenantId: string): Promise<void> {
    const tenant = this.getTenantById(account, tenantId);
    if (tenant && tenant.voucher_settings) {
      tenant.voucher_settings.vouchers_generated = 0;
      await this.updateTenant(account, tenant);
    }
  }
}
